'use client';

/**
 * Chat-tabs controller for the page.
 *
 * When tabs are on, every open chat keeps a MOUNTED pane, so a turn left
 * running in one chat keeps streaming while the user works in another. That
 * makes tab lifecycle load-bearing: a chat that no longer exists must not keep
 * a pane, and closing the pane you are looking at has to land you somewhere
 * sensible rather than on the start screen.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useChatTabs, type ChatTab } from '../../hooks/useChatTabs';
import type { Chat } from '../../types';
import { computeVisibleTabs, pickNeighbourTabId } from './tab-navigation';

export interface UseChatPageTabsParams {
  selectedChatId: string | null;
  setUrlChatId: (chatId: string | null) => void;
  handleDeleteChat: (chatId?: string) => Promise<void> | void;
  handleArchiveChat: (args: { chatId: string; chat: Chat }) => Promise<void> | void;
}

export interface ChatPageTabs {
  tabsEnabled: boolean;
  visibleTabs: ChatTab[];
  setTabTitle: (chatId: string, title: string) => void;
  handleCloseTab: (chatId: string) => void;
  handleDeleteChatAndCloseTab: (chatId?: string) => Promise<void>;
  handleArchiveChatAndCloseTab: (chatId: string, chat: Chat) => Promise<void>;
}

export function useChatPageTabs({
  selectedChatId,
  setUrlChatId,
  handleDeleteChat,
  handleArchiveChat,
}: UseChatPageTabsParams): ChatPageTabs {
  const { enabled: tabsEnabled, tabs, openTab, closeTab, setTabTitle } = useChatTabs();

  // Selecting a chat — from the sidebar, a deep link, or a newly created chat —
  // opens it as a tab.
  useEffect(() => {
    if (!tabsEnabled || !selectedChatId) return;
    openTab(selectedChatId);
  }, [tabsEnabled, selectedChatId, openTab]);

  const visibleTabs = useMemo(
    () => computeVisibleTabs(tabsEnabled, tabs, selectedChatId),
    [tabsEnabled, tabs, selectedChatId]
  );

  // Closing the tab you are looking at moves you to its neighbour; closing the
  // last one drops to the start screen.
  const handleCloseTab = useCallback(
    (chatId: string) => {
      if (chatId === selectedChatId) {
        setUrlChatId(pickNeighbourTabId(visibleTabs, chatId));
      }
      closeTab(chatId);
    },
    [selectedChatId, visibleTabs, closeTab, setUrlChatId]
  );

  // A chat that no longer exists must not keep a tab (and its mounted pane).
  const handleDeleteChatAndCloseTab = useCallback(
    async (chatId?: string) => {
      const target = chatId ?? selectedChatId;
      await handleDeleteChat(chatId);
      if (target) closeTab(target);
    },
    [handleDeleteChat, selectedChatId, closeTab]
  );

  const handleArchiveChatAndCloseTab = useCallback(
    async (chatId: string, chat: Chat) => {
      if (!chat) return;
      await handleArchiveChat({ chatId, chat });
      closeTab(chatId);
    },
    [handleArchiveChat, closeTab]
  );

  return {
    tabsEnabled,
    visibleTabs,
    setTabTitle,
    handleCloseTab,
    handleDeleteChatAndCloseTab,
    handleArchiveChatAndCloseTab,
  };
}
